import { Link } from 'react-router-dom';
import type { VideoItem } from '@/mocks/videos';

interface VideoCardProps {
  video: VideoItem;
  variant?: 'default' | 'compact' | 'featured';
}

export default function VideoCard({ video, variant = 'default' }: VideoCardProps) {
  const formatViews = (n: number): string => {
    if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return String(n);
  };

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${video.id}`}
        className="group block bg-background-50 rounded-lg overflow-hidden cursor-pointer hover:bg-background-100/80 transition-colors duration-300"
      >
        <div className="relative w-full aspect-video overflow-hidden rounded-lg">
          <img
            src={video.thumbnail}
            alt={video.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent"></div>
          <div className="absolute bottom-3 left-3 right-3">
            <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-accent-500 text-background-50 mb-2">
              {video.category}
            </span>
            <h3 className="text-base md:text-lg font-semibold text-background-50 leading-snug line-clamp-2">
              {video.title}
            </h3>
            <div className="flex items-center gap-3 mt-2 text-xs text-background-50/80">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formatViews(video.views)}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {video.duration}
              </span>
              <span>{video.publishDate}</span>
            </div>
          </div>
          <div className="absolute top-3 right-3 px-2 py-0.5 rounded text-xs font-medium bg-foreground-950/70 text-background-50">
            {video.duration}
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${video.id}`}
        className="group flex gap-3 p-2 rounded-lg cursor-pointer hover:bg-background-100/80 transition-colors duration-200"
      >
        <div className="relative w-40 sm:w-48 flex-shrink-0 aspect-video rounded-lg overflow-hidden">
          <img
            src={video.thumbnail}
            alt={video.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-foreground-950/70 text-background-50">
            {video.duration}
          </div>
        </div>
        <div className="flex flex-col justify-center min-w-0 flex-1">
          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
            {video.title}
          </h4>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
            <span className="px-1.5 py-0.5 rounded text-[10px] bg-accent-100 text-accent-700 font-medium">{video.category}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line text-[10px]"></i>
              {formatViews(video.views)}
            </span>
            <span>{video.publishDate}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${video.id}`}
      className="group block bg-background-50 rounded-lg overflow-hidden cursor-pointer border border-background-200/70 hover:border-primary-200/50 transition-all duration-300"
    >
      <div className="relative w-full aspect-video overflow-hidden">
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded text-xs font-medium bg-foreground-950/70 text-background-50">
          {video.duration}
        </div>
        {video.hotScore >= 90 && (
          <div className="absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] font-bold bg-primary-500 text-background-50">
            HOT
          </div>
        )}
      </div>
      <div className="p-3 md:p-4">
        <h3 className="text-sm md:text-base font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
          {video.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1.5 line-clamp-2 leading-relaxed">
          {video.description}
        </p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-100">
          <span className="text-[10px] md:text-xs px-2 py-0.5 rounded bg-accent-100 text-accent-700 font-medium">
            {video.category}
          </span>
          <div className="flex items-center gap-3 text-[10px] md:text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(video.views)}
            </span>
            <span>{video.publishDate}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}